import axios from 'axios';
import { apiClient } from './api';
import type {
  LeaveType,
  LeaveBalance,
  LeaveRequest,
  LeaveRequestWithUser,
  CreateLeaveRequest,
  ReviewLeaveRequest,
} from '../types/leave.types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const msg = err.response?.data?.message as string | undefined;
    return msg ?? fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export async function getLeaveTypes(): Promise<LeaveType[]> {
  try {
    const response = await apiClient.get<ApiResponse<LeaveType[]>>('/api/leave/types');
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to load leave types.'));
  }
}

export async function getMyBalances(year?: number): Promise<LeaveBalance[]> {
  try {
    const y = year ?? new Date().getFullYear();
    const response = await apiClient.get<ApiResponse<LeaveBalance[]>>(`/api/leave/balances?year=${y}`);
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to load leave balances.'));
  }
}

export async function getMyRequests(year?: number): Promise<LeaveRequest[]> {
  try {
    const y = year ?? new Date().getFullYear();
    const response = await apiClient.get<ApiResponse<LeaveRequest[]>>(`/api/leave/my-requests?year=${y}`);
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to load your leave requests.'));
  }
}

export async function submitRequest(data: CreateLeaveRequest): Promise<LeaveRequest> {
  try {
    const response = await apiClient.post<ApiResponse<LeaveRequest>>('/api/leave/request', data);
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to submit leave request. Please try again.'));
  }
}

/**
 * HR only: record leave an employee has already taken. Saved as approved and
 * counted as used straight away.
 */
export async function recordLeaveForEmployee(userId: string, data: CreateLeaveRequest): Promise<LeaveRequest> {
  try {
    const response = await apiClient.post<ApiResponse<LeaveRequest>>('/api/leave/request/on-behalf', {
      ...data,
      user_id: userId,
    });
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to record leave. Please try again.'));
  }
}

interface StaffBalanceRow {
  leave_type_id: string;
  leave_type_name: string;
  year: number;
  entitled_days: number;
  used_days: number;
  pending_days: number;
  carried_over_days: number;
}

/** HR only: one employee's balances for a year, shaped like the caller's own. */
export async function getEmployeeBalances(userId: string, year?: number): Promise<LeaveBalance[]> {
  try {
    const y = year ?? new Date().getFullYear();
    const response = await apiClient.get<ApiResponse<StaffBalanceRow[]>>(
      `/api/settings/leave-balances/${userId}?year=${y}`
    );
    return response.data.data.map((row) => ({
      id: `${userId}:${row.leave_type_id}:${row.year}`,
      user_id: userId,
      leave_type_id: row.leave_type_id,
      leave_type_name: row.leave_type_name,
      leave_type_colour: '',
      year: row.year,
      entitled_days: row.entitled_days,
      used_days: row.used_days,
      pending_days: row.pending_days,
      carried_over_days: row.carried_over_days,
      available_days: row.entitled_days + row.carried_over_days - row.used_days - row.pending_days,
    }));
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to load leave balances.'));
  }
}

export async function cancelRequest(requestId: string): Promise<void> {
  try {
    await apiClient.delete(`/api/leave/request/${requestId}`);
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to cancel leave request. Please try again.'));
  }
}

export async function getPendingRequests(): Promise<LeaveRequestWithUser[]> {
  try {
    const response = await apiClient.get<ApiResponse<LeaveRequestWithUser[]>>('/api/leave/pending');
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to load pending leave requests.'));
  }
}

export async function reviewRequest(
  requestId: string,
  review: ReviewLeaveRequest
): Promise<LeaveRequest> {
  try {
    const response = await apiClient.patch<ApiResponse<LeaveRequest>>(
      `/api/leave/request/${requestId}/review`,
      review
    );
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to submit review. Please try again.'));
  }
}

export async function getLeaveCalendar(
  month: number,
  year: number,
  branchId?: string | null
): Promise<LeaveRequestWithUser[]> {
  try {
    const url = `/api/leave/calendar?month=${month}&year=${year}${branchId ? `&branchId=${branchId}` : ''}`;
    const response = await apiClient.get<ApiResponse<LeaveRequestWithUser[]>>(url);
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to load leave calendar.'));
  }
}

/** The bank-holiday countries this company has chosen. Empty on failure, which means "no limit". */
export async function getCompanyHolidaySets(): Promise<string[]> {
  try {
    const response = await apiClient.get<ApiResponse<{ sets: string[] }>>('/api/leave/holiday-sets');
    return response.data.data.sets;
  } catch {
    return [];
  }
}

export async function getPublicHolidays(country: string, year: number, region?: string | null): Promise<string[]> {
  try {
    const params = new URLSearchParams({ country, year: String(year) });
    if (region) params.set('region', region);
    const response = await apiClient.get<ApiResponse<string[]>>(
      `/api/leave/public-holidays?${params.toString()}`
    );
    return response.data.data;
  } catch {
    return []; // graceful fallback — calendar still usable without holiday dots
  }
}

export async function calculateWorkingDays(
  startDate: string,
  endDate: string,
  country?: string | null,
  region?: string | null,
): Promise<number> {
  try {
    // `region` matters: it is what makes this count agree with the dates the
    // picker allowed. Omitting it gave Scottish and NI staff England & Wales
    // totals for a range the picker had already scoped to their own holidays.
    const params = new URLSearchParams({ startDate, endDate });
    if (country) params.set('country', country);
    if (region) params.set('region', region);
    const response = await apiClient.get<ApiResponse<{ working_days: number }>>(
      `/api/leave/calculate-days?${params.toString()}`
    );
    return response.data.data.working_days;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to calculate working days.'));
  }
}
